import { useTranslation } from "react-i18next";
import { Check, Loader2, Lock, Compass, Sparkles } from "lucide-react";
import {
  ROADMAP_PHASES,
  ROADMAP_LEGEND,
  STATUS_COLOR,
  PHASE_STATUS,
  PHASE_ACCENT,
  LONG_TERM_VISION,
  phaseProgress,
} from "@/lib/roadmapData";

/* ---------------- Status Dot ---------------- */

function StatusDot({ status, size = "sm" }) {
  const color = STATUS_COLOR[status];
  const dims = size === "lg" ? "w-5 h-5" : "w-3 h-3";
  const ring = size === "lg" ? "ring-4" : "ring-2";
  const isFuture = status === "future";
  return (
    <span
      className={`relative inline-block rounded-full ${dims} ${ring}`}
      style={{
        backgroundColor: isFuture ? "transparent" : color,
        boxShadow: isFuture ? "none" : `0 0 12px ${color}80, 0 0 24px ${color}30`,
        border: isFuture ? `1.5px solid ${color}` : `1.5px solid ${color}`,
        // ring uses outline emulation via Tailwind; we keep boxShadow for glow
      }}
    />
  );
}

/* ---------------- Phase Header Pill ---------------- */

function PhaseStatusPill({ status }) {
  const { t } = useTranslation();
  const cfg = PHASE_STATUS[status];
  const iconMap = {
    complete:           Check,
    "in-progress":      Loader2,
    "early-development": Sparkles,
    planned:            Lock,
  };
  const Icon = iconMap[status];
  const bg = `${cfg.color}1a`; // ~10% alpha
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] md:text-[11px] font-mono uppercase tracking-[0.18em] border"
      style={{ background: bg, borderColor: cfg.color, color: cfg.color }}
      data-testid={`phase-status-${status}`}
    >
      <Icon className={`w-3 h-3 ${status === "in-progress" ? "animate-spin" : ""}`} />
      {t(`roadmap.statusPills.${status}`, { defaultValue: cfg.label })}
    </span>
  );
}

/* ---------------- Legend ---------------- */

export function RoadmapLegend() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-3 md:gap-5" data-testid="roadmap-legend">
      {ROADMAP_LEGEND.map(l => {
        const labelKey = l.key === "in-progress" ? "inProgress" : l.key;
        return (
          <div key={l.key} className="flex items-center gap-2">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{
                backgroundColor: l.key === "future" ? "transparent" : l.color,
                border: `1.5px solid ${l.color}`,
                boxShadow: l.key === "future" ? "none" : `0 0 8px ${l.color}80`,
              }}
            />
            <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-[var(--t-text-dim)]">
              {t(`roadmap.legend.${labelKey === "future" ? "planned" : labelKey}`, { defaultValue: l.label })}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Phase Card ---------------- */

function PhaseCard({ phase, isLast }) {
  const { t } = useTranslation();
  const { total, done, wip, pct } = phaseProgress(phase);
  const phaseColor = PHASE_ACCENT[phase.status];
  const isDim = phase.status === "planned";

  return (
    <div className="relative pl-10 md:pl-16 pb-12 md:pb-16" data-testid={`roadmap-phase-${phase.number}`}>
      {/* Spine (vertical line) */}
      {!isLast && (
        <span
          className="absolute left-[14px] md:left-[22px] top-8 bottom-0 w-px"
          style={{
            background:
              "linear-gradient(to bottom, var(--t-blue-glow) 0%, var(--t-border-glow) 60%, transparent 100%)",
          }}
        />
      )}

      {/* Milestone dot */}
      <span
        className="absolute left-[7px] md:left-[14px] top-1 inline-flex items-center justify-center w-4 h-4 md:w-5 md:h-5 rounded-full"
        style={{
          backgroundColor: isDim ? "var(--t-bg-2)" : phaseColor,
          border: `2px solid ${phaseColor}`,
          boxShadow: isDim
            ? `0 0 0 4px rgba(94,111,140,0.08)`
            : `0 0 0 5px ${phaseColor}1f, 0 0 18px ${phaseColor}80`,
        }}
        aria-hidden
      />

      {/* Card */}
      <div className="panel corner-frame relative overflow-hidden p-5 md:p-7 lg:p-8">
        {/* Side accent stripe */}
        <span
          className="absolute left-0 top-0 bottom-0 w-[3px]"
          style={{ background: `linear-gradient(to bottom, ${phaseColor}, transparent)` }}
        />

        <div className="flex flex-wrap items-start justify-between gap-3 md:gap-4 mb-3 md:mb-4">
          <div className="min-w-0">
            <div className="font-mono text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[var(--t-cyan)] mb-1">
              // {t("roadmap.phasePrefix")} {String(phase.number).padStart(2, "0")}
            </div>
            <h3 className="font-display text-xl md:text-2xl lg:text-3xl text-[var(--t-text)] uppercase leading-tight">
              {t(`roadmap.phases.${phase.number}.title`, { defaultValue: phase.title })}
            </h3>
          </div>
          <PhaseStatusPill status={phase.status} />
        </div>

        <p className="text-sm md:text-base text-[var(--t-text-dim)] leading-relaxed mb-5 md:mb-6 max-w-3xl">
          {t(`roadmap.phases.${phase.number}.description`, { defaultValue: phase.description })}
        </p>

        {/* Progress bar */}
        <div className="mb-5 md:mb-6" data-testid={`phase-progress-${phase.number}`}>
          <div className="flex items-center justify-between mb-1.5 text-[10px] md:text-[11px] font-mono uppercase tracking-[0.2em]">
            <span className="text-[var(--t-text-dim)]">{t("roadmap.progress")}</span>
            <span style={{ color: phaseColor }}>
              {pct}%{wip ? ` · ${wip} ${t("roadmap.inBuildSuffix")}` : done ? ` · ${done}/${total} ${t("roadmap.shippedSuffix")}` : ""}
            </span>
          </div>
          <div className="h-1.5 w-full bg-[var(--t-panel-2)] border border-[var(--t-border)] overflow-hidden">
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${pct}%`,
                background: `linear-gradient(90deg, ${phaseColor}, ${phaseColor}aa)`,
                boxShadow: pct > 0 ? `0 0 10px ${phaseColor}80` : "none",
              }}
            />
          </div>
        </div>

        {/* Key Areas grid */}
        <div className="mb-5 md:mb-6">
          <div className="font-mono text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[var(--t-cyan)] mb-3">
            {t("roadmap.keyAreas")}
          </div>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
            {phase.features.map((f, idx) => (
              <li
                key={f.name}
                className="flex items-start gap-2.5 text-sm md:text-[15px] text-[var(--t-text)]"
                data-testid={`phase-${phase.number}-feature`}
              >
                <span className="mt-1.5 shrink-0">
                  <StatusDot status={f.status} />
                </span>
                <span className={f.status === "future" ? "text-[var(--t-text-dim)]" : ""}>
                  {t(`roadmap.phases.${phase.number}.features.${idx + 1}`, { defaultValue: f.name })}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Goal */}
        <div className="border-t border-dashed border-[var(--t-border)] pt-4 md:pt-5">
          <div className="font-mono text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[var(--t-cyan)] mb-1.5">
            {t("roadmap.goalLabel")}
          </div>
          <p className="text-sm md:text-base text-[var(--t-steel)] leading-relaxed italic">
            {t(`roadmap.phases.${phase.number}.goal`, { defaultValue: phase.goal })}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Long-Term Vision ---------------- */

function LongTermVision() {
  const { t } = useTranslation();
  return (
    <div className="relative pl-10 md:pl-16" data-testid="roadmap-vision">
      {/* End cap */}
      <span
        className="absolute left-[5px] md:left-[12px] top-1 inline-flex items-center justify-center w-6 h-6 md:w-7 md:h-7 rounded-full bg-[var(--t-bg-2)] border-2 border-[var(--t-cyan)]"
        style={{ boxShadow: "0 0 0 5px rgba(102,230,255,0.15), 0 0 24px rgba(102,230,255,0.6)" }}
        aria-hidden
      >
        <Compass className="w-3 h-3 md:w-3.5 md:h-3.5 text-[var(--t-cyan)]" />
      </span>

      <div className="panel panel-glow corner-frame relative overflow-hidden p-6 md:p-9 lg:p-10">
        <div className="absolute -right-24 -top-24 w-80 h-80 rounded-full bg-[radial-gradient(circle,_rgba(102,230,255,0.25)_0%,_transparent_70%)] pointer-events-none" />
        <div className="absolute -left-32 -bottom-32 w-80 h-80 rounded-full bg-[radial-gradient(circle,_rgba(58,160,255,0.20)_0%,_transparent_70%)] pointer-events-none" />

        <div className="relative">
          <div className="font-mono text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[var(--t-cyan)] mb-2">
            {t("roadmap.longTermEyebrow")}
          </div>
          <h3 className="font-display text-2xl md:text-3xl lg:text-4xl text-[var(--t-text)] uppercase leading-tight mb-4 md:mb-5">
            {t("roadmap.longTermTitle")}
          </h3>
          <p className="text-sm md:text-base lg:text-lg text-[var(--t-text-dim)] leading-relaxed max-w-3xl mb-6">
            {t("roadmap.longTermIntro", { defaultValue: LONG_TERM_VISION.intro })}
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {LONG_TERM_VISION.pillars.map((line, idx) => (
              <li
                key={line}
                className="flex items-start gap-3 font-display text-base md:text-lg text-[var(--t-text)]"
              >
                <span
                  className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-[var(--t-cyan)]"
                  style={{ boxShadow: "0 0 8px var(--t-cyan)" }}
                />
                {t(`roadmap.longTermPillars.${idx + 1}`, { defaultValue: line })}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Main Export: Full Timeline ---------------- */

export default function RoadmapTimeline({ phases }) {
  const list = phases && phases.length ? phases : ROADMAP_PHASES;
  return (
    <div className="relative" data-testid="roadmap-timeline">
      {list.map((p) => (
        <PhaseCard key={p.number} phase={p} isLast={false} />
      ))}
      <LongTermVision />
    </div>
  );
}
